import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Select, TablePagination, TableSortLabel, TextField } from '@mui/material'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import Loading from '../../components/Loading'
import { isValid, handleAxiosError, formatQuantity } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'
import forecastCalculationBasisAPI from '../../services/api/Forecasts/forecastCalculationBasisAPI'

const ShowForecast = () => {

    const { t } = useTranslation()
    const { config, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [isLoading, setIsLoading] = useState(false)
    const [calculationBasisOptions, setCalculationBasisOptions] = useState([])
    const [forecastNo, setForecastNo] = useState('')
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    /* form */
    const [customerName, setCustomerName] = useState('')
    const [siteName, setSiteName] = useState('')
    const [calculationBasis, setCalculationBasis] = useState('')
    const [periodFrom, setPeriodFrom] = useState(null)
    const [periodTo, setPeriodTo] = useState(null)
    const [forecastItems, setForecastItems] = useState([])

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/forecasts')
    }

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                goBack()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [location])

    useEffect(() => {
        getForecast()
    }, [params.id])

    /* real-time: refresh if another user updates this forecast, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-forecast-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-forecast-deleted`)

        channelUpdated.bind(`${clientId}-forecast-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getForecast()
            }
        })

        channelDeleted.bind(`${clientId}-forecast-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-forecast-updated`)
            pusher.unsubscribe(`${clientId}-forecast-deleted`)
        })
    }, [])

    useEffect(() => {
        getCalculationBasisOptions()
    }, [])

    /* requests */

    const getForecast = async () => {
        setIsLoading(true)
        await axios.get(`/api/forecasts/${params.id}`, config)
            .then(res => {
                const forecast = res.data?.data

                setForecastNo(forecast?.forecast_number ?? '')
                setCustomerName(forecast?.customer_name ?? '')
                setSiteName(forecast?.site_name ?? '')
                setCalculationBasis(forecast?.calculation_basis ?? '')
                setPeriodFrom(forecast?.period_from ? dayjs(forecast.period_from) : null)
                setPeriodTo(forecast?.period_to ? dayjs(forecast.period_to) : null)

                const items = (forecast?.forecast_items ?? []).map((item, index) => ({
                    id: item.id ?? index,
                    itemLabel: item.item_no ? `${item.item_no}${item.description ? ' - ' + item.description : ''}` : '',
                    manualOverrideQty: isValid(item.manual_override_qty) ? String(item.manual_override_qty) : '',
                    calculatedForecastQty: isValid(item.calculated_forecast_qty) ? item.calculated_forecast_qty : null,
                    firmOrderQty: isValid(item.firm_order_qty) ? item.firm_order_qty : null
                }))

                setForecastItems(items)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getCalculationBasisOptions = async () => {
        await forecastCalculationBasisAPI.getForecastCalculationBasis(config)
            .then((data) => {
                setCalculationBasisOptions(data)
            })
    }

    const rowCalculatedForecastQty = (row) => isValid(row.calculatedForecastQty) ? row.calculatedForecastQty : 0

    const rowFirmOrderQty = (row) => isValid(row.firmOrderQty) ? row.firmOrderQty : 0

    const rowFinalForecastQty = (row) => {
        if (row.manualOverrideQty !== '') {
            return Number(row.manualOverrideQty)
        }

        return rowCalculatedForecastQty(row)
    }

    const rowRemainingForecastQty = (row) => {
        return Math.max(0, rowFinalForecastQty(row) - rowFirmOrderQty(row))
    }

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const getItemSortValue = (row, property) => {
        switch (property) {
            case 'item': return row.itemLabel ?? ''
            case 'calculatedForecastQty': return rowCalculatedForecastQty(row)
            case 'manualOverrideQty': return row.manualOverrideQty !== '' ? Number(row.manualOverrideQty) : null
            case 'finalForecastQty': return rowFinalForecastQty(row)
            case 'firmOrderQty': return rowFirmOrderQty(row)
            case 'remainingForecastQty': return rowRemainingForecastQty(row)
            default: return ''
        }
    }

    const compareItemSortValues = (a, b) => {
        if (!isValid(a)) {
            return isValid(b) ? -1 : 0
        }
        if (!isValid(b)) {
            return 1
        }
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b
        }
        return String(a).localeCompare(String(b))
    }

    const sortedForecastItems = useMemo(() => {
        if (!orderBy) {
            return forecastItems
        }
        const sorted = [...forecastItems].sort((a, b) => compareItemSortValues(getItemSortValue(a, orderBy), getItemSortValue(b, orderBy)))
        return order === 'desc' ? sorted.reverse() : sorted
    }, [forecastItems, order, orderBy])

    const paginatedForecastItems = useMemo(() => (
        sortedForecastItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    ), [sortedForecastItems, page, rowsPerPage])

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedForecastItems.length / rowsPerPage) - 1)
        if (page > maxPage) {
            setPage(maxPage)
        }
    }, [sortedForecastItems, rowsPerPage])

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const totalCalculatedForecastQty = forecastItems.reduce((total, row) => total + rowCalculatedForecastQty(row), 0)

    const totalManualOverrideQty = forecastItems.reduce((total, row) => {
        return row.manualOverrideQty !== '' ? (total ?? 0) + Number(row.manualOverrideQty) : total
    }, null)

    const totalFinalForecastQty = forecastItems.reduce((total, row) => total + rowFinalForecastQty(row), 0)

    const totalFirmOrderQty = forecastItems.reduce((total, row) => total + rowFirmOrderQty(row), 0)

    const totalRemainingForecastQty = forecastItems.reduce((total, row) => total + rowRemainingForecastQty(row), 0)

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('view_forecast_no', { x: forecastNo })}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>

                <div className='p-5 pb-12'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-3 gap-x-5'>
                            {/* Customer */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('customer')}</p>
                                <Autocomplete
                                    disabled
                                    disablePortal
                                    disableClearable
                                    options={[]}
                                    value={{ label: customerName, id: null }}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Period From */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('period_from')}</p>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker disabled sx={TEXTFIELD_STYLE} format="YYYY-MM-DD" value={periodFrom} />
                                </LocalizationProvider>
                            </div>

                            {/* Period To */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('period_to')}</p>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker disabled sx={TEXTFIELD_STYLE} format="YYYY-MM-DD" value={periodTo} />
                                </LocalizationProvider>
                            </div>

                            {/* Site */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('site')}</p>
                                <Autocomplete
                                    disabled
                                    disablePortal
                                    disableClearable
                                    options={[]}
                                    value={{ label: siteName, id: null }}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Calculation Basis */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('calculation_basis')}</p>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select disabled displayEmpty value={calculationBasis} sx={TEXTFIELD_STYLE}>
                                        <MenuItem value="" disabled>{t('select_calculation_basis')}</MenuItem>
                                        {calculationBasisOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                    </div>

                    <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                        <div className='pt-1 pl-3 p-5'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('forecasts_list')}</p>
                        </div>

                        <div className='overflow-x-auto p-5 pt-0' style={{ opacity: 0.4 }}>
                            <table className='w-full text-left'>
                                <thead>
                                    <tr className='text-[#88909C] text-[13px] border-b'>
                                        <th className='py-2 pr-2 font-[400]'>
                                            <TableSortLabel active={orderBy === 'item'} direction={orderBy === 'item' ? order : 'asc'} onClick={() => handleRequestSort('item')}>{t('item')}</TableSortLabel>
                                        </th>
                                        <th className='py-2 px-2 font-[400] text-center'>
                                            <TableSortLabel active={orderBy === 'calculatedForecastQty'} direction={orderBy === 'calculatedForecastQty' ? order : 'asc'} onClick={() => handleRequestSort('calculatedForecastQty')}>{t('calculated_forecast_qty')}</TableSortLabel>
                                        </th>
                                        <th className='py-2 px-2 font-[400] text-center'>
                                            <TableSortLabel active={orderBy === 'manualOverrideQty'} direction={orderBy === 'manualOverrideQty' ? order : 'asc'} onClick={() => handleRequestSort('manualOverrideQty')}>{t('manual_override_qty')}</TableSortLabel>
                                        </th>
                                        <th className='py-2 px-2 font-[400] text-center'>
                                            <TableSortLabel active={orderBy === 'finalForecastQty'} direction={orderBy === 'finalForecastQty' ? order : 'asc'} onClick={() => handleRequestSort('finalForecastQty')}>{t('final_forecast_qty')}</TableSortLabel>
                                        </th>
                                        <th className='py-2 px-2 font-[400] text-center'>
                                            <TableSortLabel active={orderBy === 'firmOrderQty'} direction={orderBy === 'firmOrderQty' ? order : 'asc'} onClick={() => handleRequestSort('firmOrderQty')}>{t('firm_order_qty')}</TableSortLabel>
                                        </th>
                                        <th className='py-2 pl-2 font-[400] text-center'>
                                            <TableSortLabel active={orderBy === 'remainingForecastQty'} direction={orderBy === 'remainingForecastQty' ? order : 'asc'} onClick={() => handleRequestSort('remainingForecastQty')}>{t('remaining_forecast_qty')}</TableSortLabel>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedForecastItems.length <= 0 ? (
                                        <tr>
                                            <td colSpan={6} className='text-center py-4 text-[#88909C]'>{t('no_data')}</td>
                                        </tr>
                                    ) : null}
                                    {paginatedForecastItems.map((row) => (
                                        <tr key={row.id} className='border-b'>
                                            <td className='py-2 pr-2' style={{ minWidth: '220px' }}>{row.itemLabel}</td>
                                            <td className='py-2 px-2 text-center'>{formatQuantity(rowCalculatedForecastQty(row))}</td>
                                            <td className='py-2 px-2 text-center'>{row.manualOverrideQty !== '' ? formatQuantity(row.manualOverrideQty) : '-'}</td>
                                            <td className='py-2 px-2 text-center font-[700]'>{formatQuantity(rowFinalForecastQty(row))}</td>
                                            <td className='py-2 px-2 text-center'>{formatQuantity(rowFirmOrderQty(row))}</td>
                                            <td className='py-2 pl-2 text-center'>{formatQuantity(rowRemainingForecastQty(row))}</td>
                                        </tr>
                                    ))}
                                    {forecastItems.length > 0 ? (
                                        <tr className='font-[700]'>
                                            <td className='py-2 pr-2'>{t('customer_total')}</td>
                                            <td className='py-2 px-2 text-center'>{formatQuantity(totalCalculatedForecastQty)}</td>
                                            <td className='py-2 px-2 text-center'>{isValid(totalManualOverrideQty) ? formatQuantity(totalManualOverrideQty) : '-'}</td>
                                            <td className='py-2 px-2 text-center'>{formatQuantity(totalFinalForecastQty)}</td>
                                            <td className='py-2 px-2 text-center'>{formatQuantity(totalFirmOrderQty)}</td>
                                            <td className='py-2 pl-2 text-center'>{formatQuantity(totalRemainingForecastQty)}</td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>

                        {sortedForecastItems.length > 0 ? (
                            <TablePagination
                                className='table-pagination'
                                labelRowsPerPage={t('rowsPerPage')}
                                rowsPerPageOptions={[10, 15]}
                                component="div"
                                count={sortedForecastItems.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        ) : null}
                    </div>


                </div>
            </Box>
        </>
    )
}

export default ShowForecast
